/**
 * Tokens de cor do design system BYX.
 *
 * Fonte de verdade visual: projeto "Backstage byx" no Claude Design
 * (arquivo "Backstage byx.dc.html", função applyTheme()). Ao alterar
 * cores lá, refletir aqui — e vice-versa.
 */

/** Cores de marca, iguais nos dois temas. */
export const byxBrand = {
  navy: '#000949',
  indigo: '#2a02a8',
  amber: '#FCC359',
  ice: '#E3F1FF',
};

/** Sidebar é sempre indigo, nos dois temas. */
export const byxSidebar = {
  background: byxBrand.indigo,
  ink: byxBrand.ice,
  inkMuted: 'rgba(227,241,255,0.75)',
  hover: 'rgba(227,241,255,0.08)',
  divider: 'rgba(227,241,255,0.14)',
  /** Rótulo dos grupos do menu (overline); 0.65 para passar de 4.5:1. */
  groupLabel: 'rgba(227,241,255,0.65)',
};

export interface ByxColorScheme {
  bg: string;
  panel: string;
  ink: string;
  /** Ícones e traços discretos; para texto secundário use `mutedText`. */
  mute: string;
  /** Texto secundário (cabeçalhos de coluna, descrições), contraste AA. */
  mutedText: string;
  line: string;
  /** Borda de inputs, selects, caixas de filtro e botões outline. */
  borderStrong: string;
  /** Superfície secundária: linha de cabeçalho das tabelas, skeleton. */
  surface2: string;
  /** Falhou, descontinuado, estado de erro. */
  error: string;
  /** Tinta de aviso: experimental, rotas anônimas. */
  warningInk: string;
  /** Informação: API, "Só interna". */
  info: string;
  /** Tinta de sucesso (Sincronizado, produção, GET): texto legível (AA). */
  successInk: string;
  /** Fundo dos badges de sucesso. */
  successBg: string;
  /** Fundo dos badges de aviso (experimental, Fora de sincronia, PUT). */
  warningBg: string;
  /** Fundo dos badges de erro (descontinuado, Falhou, DELETE). */
  errorBg: string;
  /** Fundo dos badges de informação ("Só interna", POST). */
  infoBg: string;
  /** Fundo dos badges no accent ("Pública na internet"). */
  accentBg: string;
  /**
   * Tinta de "Pública na internet" (ponto de exposição das Rotas). No claro é
   * o accent; no escuro, um lilás claro legível sobre o painel.
   */
  publicInk: string;
  /**
   * Primary as text, links, indicators and focus rings (D-24). Never a fill
   * under white text: in the dark theme it is a light lilac.
   */
  accent: string;
  /** Filled primary surfaces that carry white text (D-24, `primary-fill`). */
  accentFill: string;
  /** Hover / pressed of a filled primary surface (`primary-fill-hover`). */
  accentFillHover: string;
  /** Text and icons on `accentFill` (`on-primary`). */
  accentInk: string;
  chip: string;
  chipInk: string;
  hover: string;
  /** Fundo de controles desabilitados (botão primário desabilitado). */
  disabled: string;
  hero: string;
  heroInk: string;
  /** Painel de logs (design system "Log panel"): fundo escuro nos dois temas. */
  log: string;
  /** Texto das linhas de log. */
  logInk: string;
  /** Carimbo de hora das linhas de log. */
  logTime: string;
  /** Numeração das linhas de log (decorativa). */
  logLineNo: string;
  /** Fundo do trecho que casa com a busca nos logs. */
  logMatch: string;
  /** Texto do trecho que casa com a busca nos logs. */
  logMatchInk: string;
  /** Barras do skeleton sobre o fundo do log. */
  logSkeleton: string;
}

export const byxLight: ByxColorScheme = {
  bg: '#f4f6fb',
  panel: '#ffffff',
  ink: '#0a0e2c',
  mute: 'rgba(10,14,44,0.55)',
  mutedText: '#5a5e74',
  line: 'rgba(10,14,44,0.12)',
  borderStrong: 'rgba(10,14,44,0.18)',
  surface2: '#f6f7fb',
  error: '#b02a20',
  warningInk: '#7a5000',
  info: '#0a66a8',
  successInk: '#1d7a50',
  successBg: 'rgba(63,178,127,0.14)',
  warningBg: 'rgba(252,195,89,0.24)',
  errorBg: 'rgba(200,54,43,0.1)',
  infoBg: 'rgba(62,142,208,0.1)',
  accentBg: 'rgba(64,3,255,0.08)',
  publicInk: '#4003FF',
  accent: '#4003FF',
  accentFill: '#4003FF',
  accentFillHover: '#3502d6',
  accentInk: '#ffffff',
  chip: '#E3F1FF',
  chipInk: '#000949',
  hover: 'rgba(64,3,255,0.05)',
  disabled: 'rgba(10,14,44,0.38)',
  hero: '#000949',
  heroInk: '#E3F1FF',
  log: '#0a0e2c',
  logInk: '#cfd6e6',
  logTime: '#8a91a6',
  logLineNo: '#6f7690',
  logMatch: 'rgba(252,195,89,0.35)',
  logMatchInk: '#ffffff',
  logSkeleton: 'rgba(207,214,230,0.12)',
};

export const byxDark: ByxColorScheme = {
  bg: '#070a12',
  panel: '#0e131e',
  ink: '#e8edf6',
  mute: 'rgba(232,237,246,0.55)',
  mutedText: '#9aa1b4',
  line: 'rgba(232,237,246,0.12)',
  borderStrong: 'rgba(227,241,255,0.16)',
  surface2: '#161a28',
  error: '#ff8a80',
  warningInk: '#FCC359',
  info: '#96BFE2',
  successInk: '#5fd39f',
  successBg: 'rgba(63,178,127,0.15)',
  warningBg: 'rgba(252,195,89,0.13)',
  errorBg: 'rgba(255,138,128,0.13)',
  infoBg: 'rgba(150,191,226,0.13)',
  accentBg: 'rgba(123,77,255,0.2)',
  publicInk: '#c4b2ff',
  // D-24 (CEO, 2026-10-25): lilac text #b9a3ff, violet fills #6f45f0.
  accent: '#b9a3ff',
  accentFill: '#6f45f0',
  accentFillHover: '#5f36e0',
  accentInk: '#ffffff',
  chip: 'rgba(150,191,226,0.14)',
  chipInk: '#a9c6e8',
  hover: 'rgba(232,237,246,0.05)',
  disabled: 'rgba(232,237,246,0.38)',
  hero: '#000949',
  heroInk: '#E3F1FF',
  log: '#080a12',
  logInk: '#cfd6e6',
  logTime: '#8a91a6',
  logLineNo: '#6f7690',
  logMatch: 'rgba(252,195,89,0.35)',
  logMatchInk: '#ffffff',
  logSkeleton: 'rgba(207,214,230,0.12)',
};

/**
 * The dark accent before D-24. Kept only as the middle stop of the progress
 * bar gradient (decorative), so the light bar does not change.
 */
export const byxViolet = '#845AF9';

/** O esquema de cores do tema ativo (`theme.palette.type`). */
export function byxScheme(type?: string): ByxColorScheme {
  return type === 'dark' ? byxDark : byxLight;
}

export interface ByxKindChip {
  ink: string;
  dot: string;
  bg: string;
  border: string;
}

/**
 * Pílulas de tipo/kind (chipPal do protótipo): service→component (accent),
 * resource (amber), demais (azul API).
 */
export const byxKindChips: Record<
  'light' | 'dark',
  Record<'component' | 'resource' | 'api', ByxKindChip>
> = {
  light: {
    component: {
      ink: '#4003FF',
      dot: '#4003FF',
      bg: 'rgba(64,3,255,0.07)',
      border: 'rgba(64,3,255,0.5)',
    },
    resource: {
      ink: '#8a5b00',
      dot: '#FCC359',
      bg: 'rgba(252,195,89,0.18)',
      border: 'rgba(214,150,30,0.6)',
    },
    api: {
      ink: '#0a66a8',
      dot: '#3E8ED0',
      bg: 'rgba(62,142,208,0.1)',
      border: 'rgba(62,142,208,0.55)',
    },
  },
  dark: {
    component: {
      ink: '#b9a3ff',
      dot: '#845AF9',
      bg: 'rgba(132,90,249,0.16)',
      border: 'rgba(132,90,249,0.55)',
    },
    resource: {
      ink: '#FCC359',
      dot: '#FCC359',
      bg: 'rgba(252,195,89,0.13)',
      border: 'rgba(252,195,89,0.5)',
    },
    api: {
      ink: '#96BFE2',
      dot: '#5b9bd5',
      bg: 'rgba(150,191,226,0.13)',
      border: 'rgba(150,191,226,0.5)',
    },
  },
};

/** Cores de estado usadas nos badges/status do protótipo. */
export const byxStatus = {
  success: '#3fb27f',
  error: '#c8362b',
  /** Âmbar da marca lido sobre fundo escuro; em fundo claro usar warningInk. */
  warning: byxBrand.amber,
  warningInk: '#d6961e',
  info: '#3e8ed0',
};
